// Template colors are intentionally fixed; PDF/DOCX outputs are theme-invariant.
// The user can pick between Light and Dark variants via the personalization tab.
import {
  AlignmentType,
  Document,
  HeadingLevel,
  ImageRun,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { ResumeDocument, ResumePersonalization } from '../../../types';
import {
  escapeHtml,
  formatPeriod,
  formatPhone,
  nl2br,
  parseImageDataUri,
  resolvePersonalization,
  scaledHalfPoints,
  scaledPx,
} from '../helpers';

const SIDEBAR_PHOTO_PX = 140;

interface Palette {
  sidebarBg: string;
  sidebarText: string;
  sidebarMuted: string;
  bodyBg: string;
  bodyText: string;
  bodyMuted: string;
  accent: string;
}

const LIGHT: Palette = {
  sidebarBg: '1E2A38',
  sidebarText: 'FFFFFF',
  sidebarMuted: 'B9C3CC',
  bodyBg: 'FFFFFF',
  bodyText: '1E2229',
  bodyMuted: '6B7280',
  accent: '2BA77F',
};

const DARK: Palette = {
  sidebarBg: '0B0D11',
  sidebarText: 'F2F3F5',
  sidebarMuted: '9AA0A6',
  bodyBg: '1A1D24',
  bodyText: 'F2F3F5',
  bodyMuted: 'A1A6AE',
  accent: '4EFFA1',
};

interface Labels {
  contact: string;
  skills: string;
  languages: string;
  links: string;
  summary: string;
  experience: string;
  education: string;
  present: string;
}

const sidebarHtml = (r: ResumeDocument, t: Labels): string => {
  const blocks: string[] = [];
  const pi = r.personalInfo;
  if (pi.photo) {
    blocks.push(`<div class="sb-photo"><img src="${pi.photo}" alt=""/></div>`);
  }
  const contact: string[] = [];
  if (pi.email) contact.push(`<div>${escapeHtml(pi.email)}</div>`);
  const phoneLine = formatPhone(pi.phoneCountryCode, pi.phone);
  if (phoneLine) contact.push(`<div>${escapeHtml(phoneLine)}</div>`);
  if (pi.location) contact.push(`<div>${escapeHtml(pi.location)}</div>`);
  if (pi.website) contact.push(`<div>${escapeHtml(pi.website)}</div>`);
  if (contact.length) {
    blocks.push(
      `<div class="sb-section"><div class="sb-h">${escapeHtml(t.contact)}</div>${contact.join('')}</div>`
    );
  }
  if (r.skills.length) {
    blocks.push(
      `<div class="sb-section"><div class="sb-h">${escapeHtml(t.skills)}</div>${r.skills
        .map((s) => `<div>• ${escapeHtml(s)}</div>`)
        .join('')}</div>`
    );
  }
  if (r.languages.length) {
    blocks.push(
      `<div class="sb-section"><div class="sb-h">${escapeHtml(t.languages)}</div>${r.languages
        .map(
          (l) =>
            `<div>${escapeHtml(l.name)}${l.proficiency ? ` — <span class="muted">${escapeHtml(l.proficiency)}</span>` : ''}</div>`
        )
        .join('')}</div>`
    );
  }
  if (r.links.length) {
    blocks.push(
      `<div class="sb-section"><div class="sb-h">${escapeHtml(t.links)}</div>${r.links
        .map(
          (l) =>
            `<div>${escapeHtml(l.label || l.url)}${l.url ? `<br/><span class="muted">${escapeHtml(l.url)}</span>` : ''}</div>`
        )
        .join('')}</div>`
    );
  }
  return blocks.join('');
};

const bodyHtml = (r: ResumeDocument, t: Labels): string => {
  const pi = r.personalInfo;
  const blocks: string[] = [];
  blocks.push(`<h1>${escapeHtml(pi.fullName || '')}</h1>`);
  if (pi.title) blocks.push(`<div class="title">${escapeHtml(pi.title)}</div>`);
  if (pi.summary) {
    blocks.push(
      `<div class="section"><div class="h">${escapeHtml(t.summary)}</div><p>${nl2br(pi.summary)}</p></div>`
    );
  }
  if (r.experience.length) {
    blocks.push(
      `<div class="section"><div class="h">${escapeHtml(t.experience)}</div>${r.experience
        .map(
          (e) => `
        <div class="item">
          <div class="row"><strong>${escapeHtml(e.position || '')}</strong><span class="muted">${escapeHtml(
            formatPeriod(e.startDate, e.endDate, e.current, t.present)
          )}</span></div>
          <div class="row"><span>${escapeHtml(e.company || '')}</span><span class="muted">${escapeHtml(e.location || '')}</span></div>
          ${e.description ? `<p>${nl2br(e.description)}</p>` : ''}
        </div>`
        )
        .join('')}</div>`
    );
  }
  if (r.education.length) {
    blocks.push(
      `<div class="section"><div class="h">${escapeHtml(t.education)}</div>${r.education
        .map(
          (e) => `
        <div class="item">
          <div class="row"><strong>${escapeHtml(e.degree || '')}${e.field ? ` — ${escapeHtml(e.field)}` : ''}</strong><span class="muted">${escapeHtml(
            formatPeriod(e.startDate, e.endDate, false, t.present)
          )}</span></div>
          <div>${escapeHtml(e.institution || '')}</div>
          ${e.description ? `<p>${nl2br(e.description)}</p>` : ''}
        </div>`
        )
        .join('')}</div>`
    );
  }
  return blocks.join('');
};

export function toHtml(
  r: ResumeDocument,
  labels: Labels,
  personalization: ResumePersonalization
): string {
  const typo = resolvePersonalization(personalization);
  const p = typo.colorMode === 'dark' ? DARK : LIGHT;

  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(r.name)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: ${typo.fontFamilyHtml}; margin: 0; background: #${p.bodyBg}; color: #${p.bodyText}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .wrap { display: flex; min-height: 100vh; }
  .sb { width: 36%; background: #${p.sidebarBg}; color: #${p.sidebarText}; padding: 32px 24px; font-size: ${scaledPx(16.8, typo.scale.body)}; line-height: 1.5; }
  .sb-photo { display: flex; justify-content: center; margin-bottom: 22px; }
  .sb-photo img { width: ${SIDEBAR_PHOTO_PX}px; height: ${SIDEBAR_PHOTO_PX}px; border-radius: 50%; object-fit: cover; border: 3px solid #${p.accent}; }
  .sb-section { margin-bottom: 22px; }
  .sb-h { text-transform: uppercase; letter-spacing: 1px; font-size: ${scaledPx(15.4, typo.scale.subtitle)}; color: #${p.accent}; font-weight: 700; margin-bottom: 8px; }
  .sb .muted { color: #${p.sidebarMuted}; }
  .body { flex: 1; padding: 36px 32px; font-size: ${scaledPx(17.5, typo.scale.body)}; line-height: 1.55; }
  h1 { margin: 0 0 4px; font-size: ${scaledPx(39.2, typo.scale.title)}; font-weight: 700; color: #${p.bodyText}; }
  .title { font-size: ${scaledPx(19.6, typo.scale.subtitle)}; color: #${p.accent}; font-weight: 600; margin-bottom: 18px; }
  .section { margin-bottom: 20px; }
  .h { text-transform: uppercase; letter-spacing: 1px; font-size: ${scaledPx(16.8, typo.scale.subtitle)}; color: #${p.accent}; font-weight: 700; margin-bottom: 10px; border-bottom: 2px solid #${p.accent}; padding-bottom: 4px; }
  .item { margin-bottom: 12px; }
  .row { display: flex; justify-content: space-between; gap: 12px; }
  .muted { color: #${p.bodyMuted}; font-size: ${scaledPx(16.1, typo.scale.body)}; }
  p { margin: 4px 0 0; white-space: pre-wrap; }
  @page { margin: 0; size: A4; }
</style></head><body>
  <div class="wrap">
    <aside class="sb">${sidebarHtml(r, labels)}</aside>
    <main class="body">${bodyHtml(r, labels)}</main>
  </div>
</body></html>`;
}

export function toDocx(
  r: ResumeDocument,
  labels: Labels,
  personalization: ResumePersonalization
): Document {
  const typo = resolvePersonalization(personalization);
  const p = typo.colorMode === 'dark' ? DARK : LIGHT;
  const font = typo.fontFamilyDocx;

  const titleSize = scaledHalfPoints(28, typo.scale.title); // 20pt base
  const subtitleSize = scaledHalfPoints(16.8, typo.scale.subtitle); // 12pt base
  const bodySize = scaledHalfPoints(15.4, typo.scale.body); // 11pt base
  const smallSize = scaledHalfPoints(14, typo.scale.body); // 10pt base
  const sidebarSize = scaledHalfPoints(12.6, typo.scale.body);
  const sidebarHeadSize = scaledHalfPoints(12.6, typo.scale.subtitle);

  const para = (
    text: string,
    opts: { bold?: boolean; size?: number; color?: string; spacing?: number } = {}
  ) =>
    new Paragraph({
      spacing: { after: opts.spacing ?? 60 },
      children: [
        new TextRun({
          text,
          bold: opts.bold,
          size: opts.size ?? bodySize,
          color: opts.color ?? p.bodyText,
          font,
        }),
      ],
    });

  const sidebarHeading = (text: string) =>
    new Paragraph({
      spacing: { before: 120, after: 60 },
      children: [
        new TextRun({
          text: text.toUpperCase(),
          bold: true,
          color: p.accent,
          size: sidebarHeadSize,
          font,
        }),
      ],
    });

  const sidebarLine = (text: string) =>
    new Paragraph({
      spacing: { after: 40 },
      children: [
        new TextRun({ text, color: p.sidebarText, size: sidebarSize, font }),
      ],
    });

  const bodyHeading = (text: string) =>
    new Paragraph({
      spacing: { before: 200, after: 80 },
      border: { bottom: { color: p.accent, style: 'single', size: 8, space: 1 } },
      children: [
        new TextRun({
          text: text.toUpperCase(),
          bold: true,
          color: p.accent,
          size: subtitleSize,
          font,
        }),
      ],
    });

  const pi = r.personalInfo;

  const sidebarChildren: Paragraph[] = [];

  const photo = parseImageDataUri(pi.photo);
  if (photo) {
    sidebarChildren.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new ImageRun({
            data: photo.base64,
            type: photo.type,
            transformation: { width: SIDEBAR_PHOTO_PX, height: SIDEBAR_PHOTO_PX },
          }),
        ],
      })
    );
  }

  const pushContact = (s: string) => {
    if (s) sidebarChildren.push(sidebarLine(s));
  };
  sidebarChildren.push(sidebarHeading(labels.contact));
  pushContact(pi.email);
  pushContact(formatPhone(pi.phoneCountryCode, pi.phone));
  pushContact(pi.location);
  pushContact(pi.website);
  if (r.skills.length) {
    sidebarChildren.push(sidebarHeading(labels.skills));
    r.skills.forEach((s) => sidebarChildren.push(sidebarLine(`• ${s}`)));
  }
  if (r.languages.length) {
    sidebarChildren.push(sidebarHeading(labels.languages));
    r.languages.forEach((l) =>
      sidebarChildren.push(
        sidebarLine(l.proficiency ? `${l.name} — ${l.proficiency}` : l.name)
      )
    );
  }
  if (r.links.length) {
    sidebarChildren.push(sidebarHeading(labels.links));
    r.links.forEach((l) => {
      if (l.label) sidebarChildren.push(sidebarLine(l.label));
      if (l.url) sidebarChildren.push(sidebarLine(l.url));
    });
  }

  const bodyChildren: Paragraph[] = [];
  bodyChildren.push(
    new Paragraph({
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.LEFT,
      children: [
        new TextRun({
          text: pi.fullName || '',
          bold: true,
          size: titleSize,
          color: p.bodyText,
          font,
        }),
      ],
    })
  );
  if (pi.title) {
    bodyChildren.push(
      new Paragraph({
        spacing: { after: 160 },
        children: [
          new TextRun({
            text: pi.title,
            color: p.accent,
            bold: true,
            size: subtitleSize,
            font,
          }),
        ],
      })
    );
  }
  if (pi.summary) {
    bodyChildren.push(bodyHeading(labels.summary));
    bodyChildren.push(para(pi.summary));
  }
  if (r.experience.length) {
    bodyChildren.push(bodyHeading(labels.experience));
    r.experience.forEach((e) => {
      bodyChildren.push(
        new Paragraph({
          spacing: { after: 30 },
          children: [
            new TextRun({
              text: e.position || '',
              bold: true,
              size: bodySize,
              color: p.bodyText,
              font,
            }),
            new TextRun({ text: '   ', font }),
            new TextRun({
              text: formatPeriod(e.startDate, e.endDate, e.current, labels.present),
              color: p.bodyMuted,
              size: smallSize,
              font,
            }),
          ],
        })
      );
      bodyChildren.push(
        new Paragraph({
          spacing: { after: 60 },
          children: [
            new TextRun({
              text: e.company || '',
              size: smallSize,
              color: p.bodyText,
              font,
            }),
            ...(e.location
              ? [
                  new TextRun({
                    text: `   ${e.location}`,
                    color: p.bodyMuted,
                    size: smallSize,
                    font,
                  }),
                ]
              : []),
          ],
        })
      );
      if (e.description) bodyChildren.push(para(e.description));
    });
  }
  if (r.education.length) {
    bodyChildren.push(bodyHeading(labels.education));
    r.education.forEach((e) => {
      const heading = e.field ? `${e.degree || ''} — ${e.field}` : e.degree || '';
      bodyChildren.push(
        new Paragraph({
          spacing: { after: 30 },
          children: [
            new TextRun({
              text: heading,
              bold: true,
              size: bodySize,
              color: p.bodyText,
              font,
            }),
            new TextRun({ text: '   ', font }),
            new TextRun({
              text: formatPeriod(e.startDate, e.endDate, false, labels.present),
              color: p.bodyMuted,
              size: smallSize,
              font,
            }),
          ],
        })
      );
      bodyChildren.push(
        new Paragraph({
          spacing: { after: 60 },
          children: [
            new TextRun({
              text: e.institution || '',
              size: smallSize,
              color: p.bodyText,
              font,
            }),
          ],
        })
      );
      if (e.description) bodyChildren.push(para(e.description));
    });
  }

  const table = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 36, type: WidthType.PERCENTAGE },
            shading: { type: ShadingType.CLEAR, color: 'auto', fill: p.sidebarBg },
            margins: { top: 400, bottom: 400, left: 360, right: 360 },
            children: sidebarChildren.length ? sidebarChildren : [new Paragraph('')],
          }),
          new TableCell({
            width: { size: 64, type: WidthType.PERCENTAGE },
            shading: { type: ShadingType.CLEAR, color: 'auto', fill: p.bodyBg },
            margins: { top: 400, bottom: 400, left: 480, right: 360 },
            children: bodyChildren.length ? bodyChildren : [new Paragraph('')],
          }),
        ],
      }),
    ],
  });

  return new Document({
    sections: [
      {
        properties: { page: { margin: { top: 0, bottom: 0, left: 0, right: 0 } } },
        children: [table],
      },
    ],
  });
}

export default {};
